import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import {
  GridCallbackDetails,
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

import { TripLeg } from '@/data/models/reservation.model';
import { TripLegStatus } from '@/data/models/trip-leg-status.model';
import { SortModel } from '@/data/models/sort-model.model';
import { TripLegSearchMenu } from '@/data/requests/trip-legs';

import { ReservationEventType } from '@/data/events/reservation';

import * as accountingSlice from '@/data/accounting.slice';
import * as lookupSlice from '@/data/lookup.slice';
import {
  fetchMonitoringTodaysTripsAsync,
  removeTripLegEventFromQueue,
  selectAllTripLegs,
  selectLoadingMonitoringTodaysTrips,
  selectMonitoringTodaysTrips,
  selectTripLegEventQueue,
} from '@/data/slices/trip-leg.slice';

import { DataGridSearch } from '../../tables/DataGridPro';
import MonitorTodaysTripsTable from './MonitorTodaysTripsTable';
import { MonitorTripsTable } from './MonitorTripsTable';
import {
  getTripLegSnapshot,
  shouldSyncTodaysTripsPage,
  TripLegSnapshot,
} from './monitorTripsRealtime';
import { DEFAULT_PAGE_SIZE } from '@/data/constants';
import { useDebounce } from '@uidotdev/usehooks';

const PREFIX = 'MonitorTrips';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  filter: `${PREFIX}-filter`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3), // Adds bottom margin to all child components except the last one
    },
    boxShadow: '0px 0px 10px #dfdfdf',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
    flexGrow: 1,
  },
  [`& .${classes.filter}`]: {
    width: 200,
  },
}));

const DEFAULT_SORT_MODEL: SortModel = {
  column: 'appointmentDate,pickUpDueTimeUTC',
  direction: 'asc,asc',
};

// 'full' fetches complete trip legs for a new page, sort, search, or filter.
// 'idsOnly' re-syncs the current page's reference IDs and pagination after a
// realtime event, since the trip leg data itself arrived with the event (see
// monitorTripsRealtime.ts).
type RefetchMode = 'full' | 'idsOnly';

export default function MonitorTrips() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const dataGridApiRef = useGridApiRef();

  const FILTER_ALL = 'ALL';
  const [currentFilter, setCurrentFilter] = useState(FILTER_ALL);
  const [searchTerm, setSearchTerm] = useState('');
  // Each request is a new object, so back-to-back requests within the
  // debounce window still change the debounced value and aren't dropped.
  const [refetchTripLegs, setRefetchTripLegs] = useState<{
    mode: RefetchMode;
  } | null>(null);
  const debouncedRefetchTripLegs = useDebounce(refetchTripLegs, 200);
  const [isSyncingIdsOnly, setIsSyncingIdsOnly] = useState(false);
  const [sortModel, setSortModel] = useState<SortModel>(DEFAULT_SORT_MODEL);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // An IDs-only sync must not abort an in-flight full fetch: it only hydrates
  // trip legs that aren't loaded yet, so it would leave the rest of the new
  // page stale. It's deferred until the full fetch finishes instead.
  const isFullFetchInFlightRef = useRef(false);
  const isIdSyncDeferredRef = useRef(false);

  const tripLegEventQueue = useAppSelector(selectTripLegEventQueue);
  const allTripLegs = useAppSelector(selectAllTripLegs);
  const monitoringTodaysTrips = useAppSelector(selectMonitoringTodaysTrips);
  const loadingMonitoringTodaysTrips = useAppSelector(
    selectLoadingMonitoringTodaysTrips,
  );

  // Each loaded trip leg's sort values and status as of the previous render,
  // so the realtime effect can tell whether an event moved a trip leg within,
  // onto, or off this page. The websocket handler enqueues an event and
  // updates its entity in the same batched render, so the realtime effect
  // sees the updated entity while this still holds the old values. It's
  // rebuilt by an effect declared after the realtime effect, so that one
  // always runs first.
  const snapshotsRef = useRef<Map<string, TripLegSnapshot>>(new Map());
  const sortColumns = sortModel.column.split(',');
  const tripLegStatusBCs =
    currentFilter === FILTER_ALL ? undefined : [currentFilter.toUpperCase()];

  const filterOptions = [
    { label: t('triplegInventory.filterAll'), value: FILTER_ALL },
    {
      label: TripLegStatus.Draft.toUpperCase(),
      value: TripLegStatus.Draft,
    },
    {
      label: TripLegStatus.OnHold.toUpperCase(),
      value: TripLegStatus.OnHold,
    },
    {
      label: TripLegStatus.Open.toUpperCase(),
      value: TripLegStatus.Open,
    },
    {
      label: TripLegStatus.Offered.toUpperCase(),
      value: TripLegStatus.Offered,
    },
    {
      label: TripLegStatus.Accepted.toUpperCase(),
      value: TripLegStatus.Accepted,
    },
    {
      label: TripLegStatus.Confirmed.toUpperCase(),
      value: TripLegStatus.Confirmed,
    },
    { label: TripLegStatus.Onsite.toUpperCase(), value: TripLegStatus.Onsite },
    {
      label: TripLegStatus.Onboard.toUpperCase(),
      value: TripLegStatus.Onboard,
    },
    {
      label: TripLegStatus.Canceled.toUpperCase(),
      value: TripLegStatus.Canceled,
    },
    { label: TripLegStatus.NoLoad.toUpperCase(), value: TripLegStatus.NoLoad },
    {
      label: TripLegStatus.Completed.toUpperCase(),
      value: TripLegStatus.Completed,
    },
    { label: TripLegStatus.Denied.toUpperCase(), value: TripLegStatus.Denied },
    {
      label: TripLegStatus.Abandoned.toUpperCase(),
      value: TripLegStatus.Abandoned,
    },
    {
      label: TripLegStatus.InTransit.toUpperCase(),
      value: TripLegStatus.InTransit,
    },
    {
      label: TripLegStatus.CanceledLate.toUpperCase(),
      value: TripLegStatus.CanceledLate,
    },
  ];

  const activeSearchRequestRef = useRef<AbortController | null>(null);
  const fetchTripLegs = useCallback(
    (syncIdsOnly: boolean) => {
      activeSearchRequestRef.current?.abort();
      const abortController = new AbortController();
      activeSearchRequestRef.current = abortController;

      dispatch(
        fetchMonitoringTodaysTripsAsync({
          page,
          pageSize,
          sort: sortModel?.column ?? undefined,
          sortOrder: sortModel?.direction ?? undefined,
          menu: TripLegSearchMenu.MONITORING_TODAYS_TRIPS,
          todayDate: DateTime.local().toFormat('yyyy-MM-dd'),
          search: searchTerm?.trim() || undefined,
          tripLegStatusBCs:
            currentFilter === FILTER_ALL
              ? undefined
              : [currentFilter.toUpperCase()],
          syncIdsOnly,
          signal: abortController.signal,
        }),
      );
    },
    [dispatch, page, pageSize, sortModel, currentFilter, searchTerm],
  );

  useEffect(() => {
    if (!debouncedRefetchTripLegs) {
      return;
    }
    const syncIdsOnly = debouncedRefetchTripLegs.mode === 'idsOnly';
    setRefetchTripLegs(null);
    if (syncIdsOnly && isFullFetchInFlightRef.current) {
      isIdSyncDeferredRef.current = true;
      return;
    }
    isFullFetchInFlightRef.current = !syncIdsOnly;
    setIsSyncingIdsOnly(syncIdsOnly);
    fetchTripLegs(syncIdsOnly);
  }, [debouncedRefetchTripLegs]);

  // Once a fetch finishes (or fails), run any IDs-only sync that was
  // deferred while a full fetch was in flight, unless another full fetch is
  // already pending.
  useEffect(() => {
    if (loadingMonitoringTodaysTrips) {
      return;
    }
    isFullFetchInFlightRef.current = false;
    if (isIdSyncDeferredRef.current) {
      isIdSyncDeferredRef.current = false;
      setRefetchTripLegs((pending) => pending ?? { mode: 'idsOnly' });
    }
  }, [loadingMonitoringTodaysTrips]);

  // Abort any in-flight request on unmount.
  useEffect(() => {
    return () => activeSearchRequestRef.current?.abort();
  }, []);

  // General refetch of page for pagination, sort, search, and filter changes.
  useEffect(() => {
    setRefetchTripLegs({ mode: 'full' });
  }, [fetchTripLegs, page, pageSize, sortModel, searchTerm, currentFilter]);

  // Real time updates. Every queued event is evaluated in one pass, since
  // removeTripLegEventFromQueue drops the whole processed batch.
  useEffect(() => {
    if (tripLegEventQueue.length === 0) {
      return;
    }
    const shouldSync = shouldSyncTodaysTripsPage({
      events: tripLegEventQueue,
      visibleTripLegReferenceIDs: monitoringTodaysTrips.items.map(
        (tripLeg) => tripLeg.tripLegReferenceID,
      ),
      tripLegsById: Object.fromEntries(
        allTripLegs.map((tripLeg) => [tripLeg.tripLegReferenceID, tripLeg]),
      ),
      previousSnapshots: snapshotsRef.current,
      sortColumns,
      tripLegStatusBCs,
      search: searchTerm,
      today: DateTime.local().toFormat('yyyy-MM-dd'),
    });
    if (shouldSync) {
      // A pending full fetch already covers this, so don't downgrade it
      setRefetchTripLegs((pending) =>
        pending?.mode === 'full' ? pending : { mode: 'idsOnly' },
      );
    }
    dispatch(removeTripLegEventFromQueue(tripLegEventQueue.length));
  }, [tripLegEventQueue, dispatch]);

  // Must stay after the realtime effect; see snapshotsRef above.
  useEffect(() => {
    snapshotsRef.current = new Map(
      allTripLegs.map((tripLeg) => [
        tripLeg.tripLegReferenceID,
        getTripLegSnapshot(tripLeg, sortColumns),
      ]),
    );
  }, [allTripLegs, sortModel]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('passengerCareLevel'));
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
  }, []);

  const handleTripLegFilterChange = (event: any) => {
    const f = event.target.value;
    setCurrentFilter(f);
  };

  const handleSearchChange = (value: string) => {
    const term = value?.trim() ?? '';
    const searchChanged = term !== (searchTerm?.trim() ?? '');
    setSearchTerm(value);
    if (searchChanged) {
      setPage(1);
    }
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== monitoringTodaysTrips.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const sortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    }
    setPage(1);
    if (newModel) {
      setSortModel(newModel);
    } else {
      setSortModel(DEFAULT_SORT_MODEL);
    }
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('monitoring.todaysTrips')}
        </Typography>
        <TextField
          select
          value={currentFilter}
          className={classes.filter}
          required={false}
          onChange={handleTripLegFilterChange}
          label={t('triplegInventory.filterStatus')}
          variant="outlined">
          {filterOptions.map((opt) => {
            return (
              <MenuItem value={opt.value} key={opt.value}>
                {opt.label}
              </MenuItem>
            );
          })}
        </TextField>
      </Box>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <DataGridSearch
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          placeholder={t('monitoring.searchPlaceholder')}
        />
      </Box>
      <MonitorTripsTable
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 40, 50, 100]}
        rowCount={monitoringTodaysTrips.itemCount}
        paginationChange={paginationChange}
        sortModelChange={sortModelChange}
        tripLegs={monitoringTodaysTrips.items}
        // IDs-only syncs update the current rows in place, so only show the
        // loading state for them if there's nothing on screen yet
        loading={
          loadingMonitoringTodaysTrips &&
          (!isSyncingIdsOnly || monitoringTodaysTrips.items.length === 0)
        }
        isSelectingTrips={false}
        showReopenAction={false}
        showInProgress={true}
        showAssignAction={false}
        showEditTimesAction={false}
        dataGridApiRef={dataGridApiRef}
      />
    </StyledBox>
  );
}
